<template>
  <div class="entity-detail-panel" :class="{ loading: loading }">
    <!-- Loading State -->
    <div v-if="loading" class="detail-loading">
      <i class="fas fa-spinner fa-spin"></i>
      <span>Loading entity details...</span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="detail-error">
      <i class="fas fa-exclamation-triangle"></i>
      <span>{{ error }}</span>
      <button @click="$emit('retry')" class="btn-retry">
        <i class="fas fa-redo"></i> Retry
      </button>
    </div>

    <!-- Content -->
    <div v-else-if="detail" class="detail-content">
      <!-- Action Bar -->
      <div class="detail-actions">
        <button
          @click="$emit('view-docs', detail.subject_id, detail.entity_name)"
          class="btn-view-docs"
          title="View documents &amp; analysis history"
        >
          <i class="fas fa-folder-open"></i>
          Docs &amp; Analysis
        </button>
        <button 
          @click="$emit('add-store', detail.entity_id)"
          class="btn-add-store"
          :title="'Add a new store/location to this entity'"
        >
          <i class="fas fa-plus-circle"></i>
          Add Store
        </button>
        <button 
          @click="archiveEntity"
          class="btn-archive"
          :disabled="archiving || detail.status === 'ARCHIVED'"
          :title="detail.status === 'ARCHIVED' ? 'Already archived' : 'Archive this store/franchisee'"
        >
          <i v-if="archiving" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-archive"></i>
          {{ archiving ? 'Archiving...' : (detail.status === 'ARCHIVED' ? 'Archived' : 'Archive Store') }}
        </button>
      </div>
      
      <!-- Executive Summary -->
      <div v-if="detail.executive_summary" class="detail-section summary-section">
        <div class="section-header">
          <i class="fas fa-file-alt"></i>
          <span>Executive Summary</span>
        </div>
        <p class="summary-text">{{ detail.executive_summary }}</p>
      </div>

      <!-- Score Breakdown -->
      <div class="detail-section scores-section">
        <div class="section-header">
          <i class="fas fa-chart-pie"></i>
          <span>Health Breakdown</span>
        </div>
        <div class="scores-grid">
          <div class="score-card">
            <div class="score-value" :class="getScoreClass(detail.overall_score)">
              {{ detail.overall_score }}%
            </div>
            <div class="score-label">Overall Score</div>
          </div>
          <div class="score-card">
            <div class="score-stat">
              <span class="stat-passing">{{ detail.passing_requirements || 0 }}</span>
              <span class="stat-divider">/</span>
              <span class="stat-total">{{ detail.total_requirements || 0 }}</span>
            </div>
            <div class="score-label">Requirements</div>
          </div>
          <div v-if="detail.waived_requirements" class="score-card">
            <div class="score-stat waived">{{ detail.waived_requirements }}</div>
            <div class="score-label">Waived</div>
          </div>
        </div>

        <!-- Coverage Breakdown -->
        <div v-if="detail.coverage_scores && Object.keys(detail.coverage_scores).length > 0" class="coverage-breakdown">
          <h5>Coverage by Type</h5>
          <div class="coverage-bars">
            <div 
              v-for="(coverage, coverageType) in detail.coverage_scores" 
              :key="coverageType"
              class="coverage-bar-item"
            >
              <div class="coverage-bar-header">
                <span class="coverage-type">{{ coverageType }}</span>
                <span class="coverage-score" :class="getScoreClass(coverage.score)">
                  {{ coverage.score }}%
                </span>
              </div>
              <div class="coverage-bar-track">
                <div 
                  class="coverage-bar-fill" 
                  :class="getScoreClass(coverage.score)"
                  :style="{ width: coverage.score + '%' }"
                ></div>
              </div>
              <div class="coverage-bar-stats">
                <span class="stat-item passing">{{ coverage.passing || 0 }} passing</span>
                <span v-if="coverage.failing" class="stat-item failing">{{ coverage.failing }} failing</span>
                <span v-if="coverage.waived" class="stat-item waived">{{ coverage.waived }} waived</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Root Causes / Issues -->
      <div v-if="detail.root_causes?.length > 0" class="detail-section issues-section">
        <div class="section-header">
          <i class="fas fa-exclamation-circle"></i>
          <span>Issues & Root Causes ({{ detail.root_causes.length }})</span>
        </div>
        <div class="issues-list">
          <div 
            v-for="(cause, idx) in detail.root_causes" 
            :key="idx"
            class="issue-card"
            :class="getIssueSeverityClass(cause)"
          >
            <div class="issue-header">
              <span class="issue-type-badge" :class="getIssueTypeClass(cause.type)">
                {{ formatIssueType(cause.type) }}
              </span>
              <span class="issue-priority" :class="getPriorityClass(cause.priority)">
                Priority: {{ cause.priority || 50 }}
              </span>
            </div>
            <div class="issue-title">{{ cause.title }}</div>
            <div v-if="cause.description" class="issue-description">
              {{ cause.description }}
            </div>
            <div v-if="cause.details" class="issue-details">
              <div v-if="cause.details.requirement_name" class="detail-item">
                <strong>Requirement:</strong> {{ cause.details.requirement_name }}
              </div>
              <div v-if="cause.details.coverage_type" class="detail-item">
                <strong>Coverage:</strong> {{ cause.details.coverage_type }}
              </div>
              <div v-if="cause.details.days_expired" class="detail-item">
                <strong>Days Expired:</strong> {{ cause.details.days_expired }}
              </div>
              <div v-if="cause.details.expiration_date" class="detail-item">
                <strong>Expired:</strong> {{ formatDate(cause.details.expiration_date) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Priority Actions -->
      <div v-if="detail.priority_actions?.length > 0" class="detail-section actions-section">
        <div class="section-header">
          <i class="fas fa-tasks"></i>
          <span>Recommended Actions</span>
        </div>
        <div class="actions-list">
          <div v-for="(action, idx) in detail.priority_actions" :key="idx" class="action-item">
            <span class="action-number">{{ idx + 1 }}</span>
            <span class="action-text">{{ action }}</span>
          </div>
        </div>
      </div>

      <!-- Upcoming Expirations -->
      <div v-if="detail.upcoming_expirations?.length > 0" class="detail-section expirations-section">
        <div class="section-header">
          <i class="fas fa-calendar-exclamation"></i>
          <span>Upcoming Expirations</span>
        </div>
        <div class="expirations-list">
          <div 
            v-for="(exp, idx) in detail.upcoming_expirations" 
            :key="idx"
            class="expiration-item"
            :class="getExpirationUrgency(exp)"
          >
            <div class="expiration-date">
              <i class="fas fa-calendar-alt"></i>
              {{ formatDate(exp.date || exp.expiration_date) }}
            </div>
            <div class="expiration-info">
              <div class="expiration-name">{{ exp.requirement_name || exp.coverage_type || 'Unknown' }}</div>
              <div v-if="exp.days_until" class="expiration-days">
                {{ exp.days_until }} days remaining
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Trend Analysis -->
      <div v-if="detail.trend_analysis" class="detail-section trend-section">
        <div class="section-header">
          <i class="fas fa-chart-line"></i>
          <span>Trend Analysis</span>
        </div>
        <p class="trend-text">{{ detail.trend_analysis }}</p>
        <div v-if="detail.score_30_days || detail.score_60_days || detail.score_90_days" class="trend-projections">
          <div v-if="detail.score_30_days" class="projection-item">
            <span class="projection-label">30-Day Projection:</span>
            <span class="projection-value" :class="getScoreClass(detail.score_30_days)">
              {{ detail.score_30_days }}%
            </span>
          </div>
          <div v-if="detail.score_60_days" class="projection-item">
            <span class="projection-label">60-Day Projection:</span>
            <span class="projection-value" :class="getScoreClass(detail.score_60_days)">
              {{ detail.score_60_days }}%
            </span>
          </div>
          <div v-if="detail.score_90_days" class="projection-item">
            <span class="projection-label">90-Day Projection:</span>
            <span class="projection-value" :class="getScoreClass(detail.score_90_days)">
              {{ detail.score_90_days }}%
            </span>
          </div>
        </div>
      </div>

      <!-- Compliance Configuration (Profile, Waivers, Limit Overrides) -->
      <div class="detail-section config-section">
        <div class="section-header">
          <i class="fas fa-sliders-h"></i>
          <span>Compliance Configuration</span>
        </div>

        <!-- Contractor Profile -->
        <div class="config-row">
          <div class="config-label">
            <i class="fas fa-tag"></i>
            Contractor Profile
          </div>
          <div class="config-value">
            <span v-if="subjectDetail && subjectDetail.contractor_profile_name" class="profile-badge">
              {{ subjectDetail.contractor_profile_name }}
            </span>
            <span v-else class="config-empty">Default (No Profile)</span>
            <button class="btn-config-edit" @click="showProfileModal = true" title="Change contractor profile">
              <i class="fas fa-edit"></i>
            </button>
          </div>
        </div>

        <!-- Active Waivers -->
        <div class="config-row">
          <div class="config-label">
            <i class="fas fa-shield-check"></i>
            Active Waivers
          </div>
          <div class="config-value">
            <span class="waiver-count-badge" :class="{ 'has-waivers': activeWaivers.length > 0 }">
              {{ activeWaivers.length }} active
            </span>
            <button class="btn-config-edit" @click="showWaiverModal = true" title="Manage waivers">
              <i class="fas fa-edit"></i>
            </button>
          </div>
        </div>

        <!-- Active waiver list (compact) -->
        <div v-if="activeWaivers.length > 0" class="waiver-list-compact">
          <div v-for="waiver in activeWaivers.slice(0, 3)" :key="waiver.id" class="waiver-chip">
            <span class="waiver-code">{{ waiver.requirement_name || waiver.requirement_code }}</span>
            <span v-if="waiver.expires_at" class="waiver-expiry">
              until {{ formatDate(waiver.expires_at) }}
            </span>
            <button class="btn-revoke-inline" @click="revokeWaiver(waiver)" title="Revoke waiver">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <button v-if="activeWaivers.length > 3" class="btn-see-all" @click="showWaiverModal = true">
            + {{ activeWaivers.length - 3 }} more...
          </button>
        </div>

        <div v-if="agentPolicyNotes.length" class="agent-policy-notes">
          <div class="config-label">
            <i class="fas fa-comment-dots"></i>
            Agent said they do not provide
          </div>
          <p class="agent-policy-hint">
            The insurance agent reported this on the secure upload. It does not accept the gap. Create a waiver if your team is accepting the missing coverage.
          </p>
          <div
            v-for="note in agentPolicyNotes"
            :key="note.id"
            class="agent-policy-note"
          >
            <strong>{{ note.policy_label }}</strong>
            <span>{{ note.disposition_label }}</span>
            <span v-if="note.requirement_status_label">· {{ note.requirement_status_label }}</span>
            <span v-if="note.note">· {{ note.note }}</span>
            <span v-if="note.actor_email">· {{ note.actor_email }}</span>
          </div>
          <div v-if="latestAudit" class="agent-policy-note">
            <strong>Quality audit</strong>
            <span>{{ latestAudit.summary }}</span>
            <span v-if="latestAudit.follow_up">· {{ latestAudit.follow_up }}</span>
          </div>
        </div>

        <!-- Coverage Limit Overrides (policy_minimums) -->
        <div class="config-row config-row-top">
          <div class="config-label">
            <i class="fas fa-dollar-sign"></i>
            Limit Overrides
          </div>
          <div class="config-value">
            <span v-if="hasLimitOverrides" class="override-count-badge">
              {{ Object.keys(subjectDetail.policy_minimums || {}).length }} coverage(s) overridden
            </span>
            <span v-else class="config-empty">Using defaults</span>
            <button class="btn-config-edit" @click="showLimitsModal = true" title="Edit limit overrides">
              <i class="fas fa-edit"></i>
            </button>
          </div>
        </div>

        <!-- Limit overrides display (compact) -->
        <div v-if="hasLimitOverrides" class="limits-compact">
          <div v-for="(limits, coverage) in subjectDetail.policy_minimums" :key="coverage" class="limit-chip">
            <span class="limit-coverage">{{ coverage }}</span>
            <span v-for="(val, key) in limits" :key="key" class="limit-value">
              {{ formatLimitKey(key) }}: ${{ formatMoney(val) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Metadata -->
      <div class="detail-section metadata-section">
        <div class="metadata-items">
          <span class="metadata-item">
            <i class="fas fa-clock"></i>
            Updated: {{ formatDateTime(detail.calculated_at) }}
          </span>
          <span v-if="detail.llm_model_used" class="metadata-item">
            <i class="fas fa-robot"></i>
            Model: {{ detail.llm_model_used }}
          </span>
        </div>
      </div>
    </div>

    <!-- Waiver Management Modal -->
    <div v-if="showWaiverModal" class="modal-overlay" @click.self="showWaiverModal = false">
      <div class="config-modal">
        <div class="modal-header">
          <h3><i class="fas fa-shield-check"></i> Manage Waivers</h3>
          <button class="btn-modal-close" @click="showWaiverModal = false"><i class="fas fa-times"></i></button>
        </div>
        <div class="modal-body">
          <!-- Active waivers -->
          <div v-if="activeWaivers.length > 0" class="waiver-table">
            <h4>Active Waivers</h4>
            <div v-for="waiver in activeWaivers" :key="waiver.id" class="waiver-row">
              <div class="waiver-row-main">
                <strong>{{ waiver.requirement_name || waiver.requirement_code }}</strong>
                <span class="waiver-scope-badge">{{ waiver.scope }}</span>
              </div>
              <div class="waiver-row-meta">
                <span>Reason: {{ waiver.reason || '(none)' }}</span>
                <span v-if="waiver.expires_at">· Expires: {{ formatDate(waiver.expires_at) }}</span>
                <span>· By: {{ waiver.waived_by_username || 'System' }}</span>
              </div>
              <button class="btn-revoke" @click="revokeWaiver(waiver)" :disabled="revokingId === waiver.id">
                <i :class="revokingId === waiver.id ? 'fas fa-spinner fa-spin' : 'fas fa-ban'"></i>
                {{ revokingId === waiver.id ? 'Revoking...' : 'Revoke' }}
              </button>
            </div>
          </div>
          <div v-else class="empty-waivers">
            <i class="fas fa-info-circle"></i> No active waivers for this entity.
          </div>

          <!-- Add new waiver -->
          <div class="add-waiver-form">
            <h4>Add New Waiver</h4>
            <div class="form-group">
              <label>Requirement Code</label>
              <input v-model="newWaiver.requirement_code" placeholder="e.g. AUTO_LIABILITY_CHECK" class="form-input" />
            </div>
            <div class="form-group">
              <label>Reason (required)</label>
              <textarea v-model="newWaiver.reason" placeholder="Explain why this requirement is being waived..." class="form-textarea" rows="2"></textarea>
            </div>
            <div class="form-group">
              <label>Expires On (optional — leave blank for permanent)</label>
              <input v-model="newWaiver.expires_at" type="date" class="form-input" />
            </div>
            <button class="btn-add-waiver" @click="createWaiver" :disabled="creatingWaiver || !newWaiver.requirement_code || !newWaiver.reason">
              <i :class="creatingWaiver ? 'fas fa-spinner fa-spin' : 'fas fa-plus'"></i>
              {{ creatingWaiver ? 'Saving...' : 'Create Waiver' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Profile Assignment Modal -->
    <div v-if="showProfileModal" class="modal-overlay" @click.self="showProfileModal = false">
      <div class="config-modal config-modal-sm">
        <div class="modal-header">
          <h3><i class="fas fa-tag"></i> Assign Contractor Profile</h3>
          <button class="btn-modal-close" @click="showProfileModal = false"><i class="fas fa-times"></i></button>
        </div>
        <div class="modal-body">
          <p class="modal-hint">Profiles define which coverage types are required for this contractor category (Layer 2 of policy hierarchy).</p>
          <div class="profile-options">
            <label class="profile-option" :class="{ selected: selectedProfileId === null }">
              <input type="radio" :value="null" v-model="selectedProfileId" />
              <span class="profile-option-name">Default (No Profile)</span>
              <span class="profile-option-desc">Uses project baseline requirements only</span>
            </label>
            <label v-for="profile in availableProfiles" :key="profile.id" class="profile-option" :class="{ selected: selectedProfileId === profile.id }">
              <input type="radio" :value="profile.id" v-model="selectedProfileId" />
              <span class="profile-option-name">{{ profile.category_name }}</span>
              <span v-if="profile.description" class="profile-option-desc">{{ profile.description }}</span>
              <span v-if="profile.required_coverages?.length" class="profile-option-coverages">
                Requires: {{ profile.required_coverages.join(', ') }}
              </span>
            </label>
          </div>
          <div v-if="availableProfiles.length === 0" class="empty-profiles">
            <i class="fas fa-info-circle"></i>
            No profiles configured for this project yet.
            <a href="#" @click.prevent="showProfileModal = false; $emit('open-profile-settings')">Configure profiles</a>
          </div>
          <button class="btn-save-profile" @click="saveProfile" :disabled="savingProfile">
            <i :class="savingProfile ? 'fas fa-spinner fa-spin' : 'fas fa-save'"></i>
            {{ savingProfile ? 'Saving...' : 'Save Profile' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Limit Overrides Modal -->
    <div v-if="showLimitsModal" class="modal-overlay" @click.self="showLimitsModal = false">
      <div class="config-modal">
        <div class="modal-header">
          <h3><i class="fas fa-dollar-sign"></i> Coverage Limit Overrides</h3>
          <button class="btn-modal-close" @click="showLimitsModal = false"><i class="fas fa-times"></i></button>
        </div>
        <div class="modal-body">
          <p class="modal-hint">
            Override specific coverage limits for this entity (Layer 3 — most specific). 
            These override both project defaults and contractor profile settings.
          </p>
          <div class="limits-editor">
            <div v-for="(limits, coverage) in limitsEdit" :key="coverage" class="limit-edit-row">
              <div class="limit-edit-header">
                <span class="limit-coverage-label">{{ coverage }}</span>
                <button class="btn-remove-coverage" @click="removeCoverageOverride(coverage)" title="Remove override for this coverage">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
              <div v-for="(val, limitKey) in limits" :key="limitKey" class="limit-edit-field">
                <label>{{ formatLimitKey(limitKey) }}</label>
                <input :value="val" @input="updateLimitValue(coverage, limitKey, $event.target.value)" type="number" class="form-input-sm" placeholder="0" />
                <button class="btn-remove-limit" @click="removeLimitField(coverage, limitKey)" title="Remove this limit">
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <button class="btn-add-limit-field" @click="addLimitField(coverage)">
                <i class="fas fa-plus"></i> Add limit
              </button>
            </div>

            <!-- Add new coverage override -->
            <div class="add-coverage-row">
              <select v-model="newCoverageCode" class="form-select">
                <option value="">+ Add coverage override...</option>
                <option value="GL">GL (General Liability)</option>
                <option value="WC">WC (Workers Comp)</option>
                <option value="AUTO">AUTO (Auto Liability)</option>
                <option value="UMBRELLA">UMBRELLA</option>
                <option value="PROF_LIABILITY">PROF_LIABILITY (Professional)</option>
                <option value="EPLI">EPLI (Employment Practices)</option>
                <option value="CYBER">CYBER</option>
              </select>
              <button v-if="newCoverageCode" class="btn-add-coverage" @click="addCoverageOverride">
                <i class="fas fa-plus"></i>
              </button>
            </div>
          </div>
          <div class="modal-actions">
            <button class="btn-clear-overrides" @click="clearAllOverrides" :disabled="savingLimits">
              Clear All Overrides
            </button>
            <button class="btn-save-limits" @click="saveLimits" :disabled="savingLimits">
              <i :class="savingLimits ? 'fas fa-spinner fa-spin' : 'fas fa-save'"></i>
              {{ savingLimits ? 'Saving...' : 'Save Overrides' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- No Data State -->
    <div v-else class="detail-empty">
      <i class="fas fa-info-circle"></i>
      <span>No detailed information available</span>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import api from '@/services/api'

export default {
  name: 'EntityDetailPanel',
  
  props: {
    detail: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    }
  },

  emits: ['retry', 'archived', 'add-store', 'view-docs', 'open-profile-settings'],

  setup(props, { emit }) {
    const archiving = ref(false)

    // --- Waiver state ---
    const activeWaivers = ref([])
    const agentPolicyNotes = ref([])
    const latestAudit = ref(null)
    const showWaiverModal = ref(false)
    const revokingId = ref(null)
    const creatingWaiver = ref(false)
    const newWaiver = ref({ requirement_code: '', reason: '', expires_at: '' })

    // --- Profile state ---
    const subjectDetail = ref(null)
    const availableProfiles = ref([])
    const showProfileModal = ref(false)
    const selectedProfileId = ref(null)
    const savingProfile = ref(false)

    // --- Limits state ---
    const showLimitsModal = ref(false)
    const limitsEdit = ref({})
    const newCoverageCode = ref('')
    const savingLimits = ref(false)

    const hasLimitOverrides = computed(() =>
      subjectDetail.value &&
      subjectDetail.value.policy_minimums &&
      Object.keys(subjectDetail.value.policy_minimums).length > 0
    )

    const loadSubjectData = async () => {
      if (!props.detail?.subject_id) return
      try {
        const [subjectRes, waiversRes] = await Promise.all([
          api.get(`/compliance-subjects/${props.detail.subject_id}/`),
          api.get(`/compliance-subjects/${props.detail.subject_id}/active_waivers/`)
        ])
        subjectDetail.value = subjectRes.data
        activeWaivers.value = waiversRes.data || []
        try {
          const notesRes = await api.get(
            `/compliance-subjects/${props.detail.subject_id}/agent_policy_dispositions/`
          )
          agentPolicyNotes.value = notesRes.data || []
        } catch (notesErr) {
          agentPolicyNotes.value = []
          console.error('Failed to load agent policy notes:', notesErr)
        }
        try {
          const auditRes = await api.get(
            `/compliance-subjects/${props.detail.subject_id}/agent-feedback-audit/`
          )
          latestAudit.value = auditRes.data || null
        } catch {
          latestAudit.value = null
        }
        selectedProfileId.value = subjectDetail.value.contractor_profile || null
        limitsEdit.value = JSON.parse(JSON.stringify(subjectDetail.value.policy_minimums || {}))

        // Load available profiles for this project
        if (subjectDetail.value.project) {
          const profilesRes = await api.get(`/contractor-profiles/?project=${subjectDetail.value.project}`)
          availableProfiles.value = profilesRes.data?.results || profilesRes.data || []
        }
      } catch (err) {
        console.error('Failed to load subject config data:', err)
      }
    }

    watch(() => props.detail?.subject_id, (newId) => {
      if (newId) loadSubjectData()
    }, { immediate: true })

    const revokeWaiver = async (waiver) => {
      if (!confirm(`Revoke waiver for "${waiver.requirement_name || waiver.requirement_code}"?`)) return
      revokingId.value = waiver.id
      try {
        await api.post(`/compliance-waivers/${waiver.id}/revoke/`, { reason: 'Manually revoked' })
        activeWaivers.value = activeWaivers.value.filter(w => w.id !== waiver.id)
        if (window.$toast) window.$toast.success('Waiver revoked')
      } catch (err) {
        console.error('Error revoking waiver:', err)
        if (window.$toast) window.$toast.error('Failed to revoke waiver')
      } finally {
        revokingId.value = null
      }
    }

    const createWaiver = async () => {
      if (!newWaiver.value.requirement_code || !newWaiver.value.reason) return
      creatingWaiver.value = true
      try {
        const payload = {
          compliance_subject: props.detail.subject_id,
          requirement_code: newWaiver.value.requirement_code.trim(),
          reason: newWaiver.value.reason,
          scope: 'SUBJECT',
        }
        if (newWaiver.value.expires_at) payload.expires_at = newWaiver.value.expires_at
        const res = await api.post('/compliance-waivers/', payload)
        activeWaivers.value.unshift(res.data)
        newWaiver.value = { requirement_code: '', reason: '', expires_at: '' }
        if (window.$toast) window.$toast.success('Waiver created')
      } catch (err) {
        console.error('Error creating waiver:', err)
        if (window.$toast) window.$toast.error('Failed to create waiver')
      } finally {
        creatingWaiver.value = false
      }
    }

    const saveProfile = async () => {
      savingProfile.value = true
      try {
        const res = await api.patch(
          `/compliance-subjects/${props.detail.subject_id}/update_profile/`,
          { contractor_profile: selectedProfileId.value }
        )
        subjectDetail.value = res.data
        showProfileModal.value = false
        if (window.$toast) window.$toast.success('Contractor profile updated')
      } catch (err) {
        console.error('Error saving profile:', err)
        if (window.$toast) window.$toast.error('Failed to save profile')
      } finally {
        savingProfile.value = false
      }
    }

    const updateLimitValue = (coverage, limitKey, value) => {
      const num = parseFloat(value)
      if (!isNaN(num)) limitsEdit.value[coverage][limitKey] = num
    }

    const removeLimitField = (coverage, limitKey) => {
      const limits = { ...limitsEdit.value[coverage] }
      delete limits[limitKey]
      limitsEdit.value[coverage] = limits
    }

    const removeCoverageOverride = (coverage) => {
      const updated = { ...limitsEdit.value }
      delete updated[coverage]
      limitsEdit.value = updated
    }

    const addCoverageOverride = () => {
      if (!newCoverageCode.value) return
      if (!limitsEdit.value[newCoverageCode.value]) {
        limitsEdit.value[newCoverageCode.value] = { per_occurrence: 1000000 }
      }
      newCoverageCode.value = ''
    }

    const addLimitField = (coverage) => {
      const newKey = prompt('Enter limit key (e.g. per_occurrence, aggregate, per_accident):')
      if (newKey) {
        limitsEdit.value[coverage] = { ...limitsEdit.value[coverage], [newKey]: 0 }
      }
    }

    const clearAllOverrides = async () => {
      if (!confirm('Clear all coverage limit overrides for this entity? It will use project/profile defaults.')) return
      limitsEdit.value = {}
      await saveLimits()
    }

    const saveLimits = async () => {
      savingLimits.value = true
      try {
        const res = await api.patch(
          `/compliance-subjects/${props.detail.subject_id}/update_policy/`,
          { policy_minimums: limitsEdit.value }
        )
        subjectDetail.value = res.data
        showLimitsModal.value = false
        if (window.$toast) window.$toast.success('Limit overrides saved')
      } catch (err) {
        console.error('Error saving limits:', err)
        if (window.$toast) window.$toast.error('Failed to save limit overrides')
      } finally {
        savingLimits.value = false
      }
    }

    const archiveEntity = async () => {
      if (!props.detail || !props.detail.subject_id) {
        return
      }
      
      const entityName = props.detail.entity_name || 'this entity'
      const storeInfo = props.detail.store_number ? ` (Store #${props.detail.store_number})` : ''
      
      if (!confirm(`Are you sure you want to archive ${entityName}${storeInfo}? This will mark it as closed.`)) {
        return
      }
      
      archiving.value = true
      
      try {
        const response = await api.post(
          `/compliance-health/entities/${props.detail.subject_id}/archive/`,
          {
            reason: 'Store closed',
            close_date: new Date().toISOString().split('T')[0]
          }
        )
        
        if (response.data.success) {
          if (window.$toast) {
            window.$toast.success(response.data.message)
          }
          emit('archived', props.detail.subject_id)
        }
      } catch (error) {
        console.error('Error archiving entity:', error)
        if (window.$toast) {
          window.$toast.error('Failed to archive: ' + (error.response?.data?.error || error.message))
        }
      } finally {
        archiving.value = false
      }
    }

    const formatMoney = (val) => {
      if (val == null) return '0'
      return Number(val).toLocaleString()
    }

    const formatLimitKey = (key) => {
      return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    const getScoreClass = (score) => {
      if (score >= 90) return 'excellent'
      if (score >= 75) return 'good'
      if (score >= 50) return 'warning'
      if (score >= 25) return 'at-risk'
      return 'critical'
    }

    const getIssueSeverityClass = (cause) => {
      const priority = cause.priority || 50
      if (priority >= 90 || cause.type?.includes('CRITICAL') || cause.type?.includes('EXPIRED')) return 'severity-critical'
      if (priority >= 70) return 'severity-high'
      if (priority >= 50) return 'severity-medium'
      return 'severity-low'
    }

    const getIssueTypeClass = (type) => {
      if (!type) return ''
      const lowerType = type.toLowerCase()
      if (lowerType.includes('expired')) return 'type-expired'
      if (lowerType.includes('missing') || lowerType.includes('no_policy')) return 'type-missing'
      if (lowerType.includes('gap') || lowerType.includes('coverage_gap')) return 'type-gap'
      if (lowerType.includes('endorsement')) return 'type-endorsement'
      return 'type-other'
    }

    const formatIssueType = (type) => {
      if (!type) return 'Issue'
      return type
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase())
    }

    const getPriorityClass = (priority) => {
      if (priority >= 90) return 'priority-critical'
      if (priority >= 70) return 'priority-high'
      if (priority >= 50) return 'priority-medium'
      return 'priority-low'
    }

    const getExpirationUrgency = (exp) => {
      const days = exp.days_until || 30
      if (days <= 7) return 'urgency-critical'
      if (days <= 14) return 'urgency-high'
      if (days <= 30) return 'urgency-medium'
      return 'urgency-low'
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return 'N/A'
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      })
    }

    const formatDateTime = (dateStr) => {
      if (!dateStr) return 'N/A'
      const date = new Date(dateStr)
      return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    }

    return {
      archiving,
      archiveEntity,
      // Waiver
      activeWaivers,
      agentPolicyNotes,
      latestAudit,
      showWaiverModal,
      revokingId,
      creatingWaiver,
      newWaiver,
      revokeWaiver,
      createWaiver,
      // Profile
      subjectDetail,
      availableProfiles,
      showProfileModal,
      selectedProfileId,
      savingProfile,
      saveProfile,
      // Limits
      showLimitsModal,
      limitsEdit,
      newCoverageCode,
      savingLimits,
      hasLimitOverrides,
      updateLimitValue,
      removeLimitField,
      removeCoverageOverride,
      addCoverageOverride,
      addLimitField,
      clearAllOverrides,
      saveLimits,
      formatMoney,
      formatLimitKey,
      // Existing
      getScoreClass,
      getIssueSeverityClass,
      getIssueTypeClass,
      formatIssueType,
      getPriorityClass,
      getExpirationUrgency,
      formatDate,
      formatDateTime
    }
  }
}
</script>

<style scoped>
.entity-detail-panel {
  background: #f8fafc;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 0.75rem;
  border: 1px solid #e2e8f0;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.entity-detail-panel.loading {
  min-height: 100px;
}

/* Loading State */
.detail-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem;
  color: #64748b;
  font-size: 0.875rem;
}

.detail-loading i {
  color: #6366f1;
}

/* Error State */
.detail-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: #fef2f2;
  border-radius: 6px;
  color: #dc2626;
  font-size: 0.875rem;
}

.btn-retry {
  margin-left: auto;
  padding: 0.25rem 0.5rem;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.btn-retry:hover {
  background: #b91c1c;
}

/* Content */
.detail-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Action Bar */
.detail-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 0.5rem;
}

.btn-view-docs {
  padding: 0.5rem 1rem;
  background: #eef2ff;
  color: #4338ca;
  border: 1px solid #c7d2fe;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.btn-view-docs:hover {
  background: #e0e7ff;
  border-color: #a5b4fc;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(99, 102, 241, 0.15);
}

.btn-add-store {
  padding: 0.5rem 1rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.btn-add-store:hover {
  background: #059669;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-archive {
  padding: 0.5rem 1rem;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.btn-archive:hover:not(:disabled) {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-archive:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Sections */
.detail-section {
  background: white;
  border-radius: 6px;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #334155;
  font-size: 0.8rem;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section-header i {
  color: #6366f1;
  font-size: 0.75rem;
}

/* Summary Section */
.summary-text {
  margin: 0;
  font-size: 0.85rem;
  color: #475569;
  line-height: 1.5;
}

/* Scores Section */
.scores-grid {
  display: flex;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.score-card {
  text-align: center;
  padding: 0.5rem 1rem;
  background: #f1f5f9;
  border-radius: 6px;
  flex: 1;
}

.score-value {
  font-size: 1.25rem;
  font-weight: 700;
}

.score-value.excellent { color: #059669; }
.score-value.good { color: #2563eb; }
.score-value.warning { color: #d97706; }
.score-value.at-risk { color: #ea580c; }
.score-value.critical { color: #dc2626; }

.score-stat {
  font-size: 1.1rem;
  font-weight: 600;
}

.stat-passing { color: #059669; }
.stat-divider { color: #94a3b8; margin: 0 0.125rem; }
.stat-total { color: #64748b; }
.stat-waived { color: #8b5cf6; }

.score-label {
  font-size: 0.65rem;
  color: #64748b;
  text-transform: uppercase;
  margin-top: 0.25rem;
}

/* Coverage Breakdown */
.coverage-breakdown h5 {
  font-size: 0.75rem;
  color: #64748b;
  margin: 0 0 0.5rem 0;
  font-weight: 500;
}

.coverage-bars {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.coverage-bar-item {
  background: #f8fafc;
  padding: 0.5rem;
  border-radius: 4px;
}

.coverage-bar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
}

.coverage-type {
  font-size: 0.75rem;
  font-weight: 600;
  color: #334155;
  text-transform: uppercase;
}

.coverage-score {
  font-size: 0.75rem;
  font-weight: 700;
}

.coverage-score.excellent { color: #059669; }
.coverage-score.good { color: #2563eb; }
.coverage-score.warning { color: #d97706; }
.coverage-score.at-risk { color: #ea580c; }
.coverage-score.critical { color: #dc2626; }

.coverage-bar-track {
  height: 6px;
  background: #e2e8f0;
  border-radius: 3px;
  overflow: hidden;
}

.coverage-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.coverage-bar-fill.excellent { background: #10b981; }
.coverage-bar-fill.good { background: #3b82f6; }
.coverage-bar-fill.warning { background: #f59e0b; }
.coverage-bar-fill.at-risk { background: #f97316; }
.coverage-bar-fill.critical { background: #ef4444; }

.coverage-bar-stats {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.stat-item {
  font-size: 0.65rem;
}

.stat-item.passing { color: #059669; }
.stat-item.failing { color: #dc2626; }
.stat-item.waived { color: #8b5cf6; }

/* Issues Section */
.issues-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.issue-card {
  padding: 0.75rem;
  border-radius: 6px;
  border-left: 3px solid #e2e8f0;
  background: #fafafa;
}

.issue-card.severity-critical {
  border-left-color: #dc2626;
  background: #fef2f2;
}

.issue-card.severity-high {
  border-left-color: #f97316;
  background: #fff7ed;
}

.issue-card.severity-medium {
  border-left-color: #f59e0b;
  background: #fffbeb;
}

.issue-card.severity-low {
  border-left-color: #94a3b8;
  background: #f8fafc;
}

.issue-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.375rem;
}

.issue-type-badge {
  font-size: 0.6rem;
  padding: 0.125rem 0.375rem;
  border-radius: 3px;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.issue-type-badge.type-expired { background: #fee2e2; color: #991b1b; }
.issue-type-badge.type-missing { background: #fef3c7; color: #92400e; }
.issue-type-badge.type-gap { background: #ffedd5; color: #9a3412; }
.issue-type-badge.type-endorsement { background: #dbeafe; color: #1e40af; }
.issue-type-badge.type-other { background: #f3f4f6; color: #374151; }

.issue-priority {
  font-size: 0.6rem;
  color: #64748b;
  margin-left: auto;
}

.issue-priority.priority-critical { color: #dc2626; font-weight: 600; }
.issue-priority.priority-high { color: #ea580c; }
.issue-priority.priority-medium { color: #d97706; }
.issue-priority.priority-low { color: #64748b; }

.issue-title {
  font-size: 0.8rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
}

.issue-description {
  font-size: 0.75rem;
  color: #64748b;
  margin-bottom: 0.25rem;
}

.issue-details {
  font-size: 0.7rem;
  color: #475569;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.detail-item {
  background: rgba(255, 255, 255, 0.7);
  padding: 0.125rem 0.375rem;
  border-radius: 3px;
}

/* Actions Section */
.actions-list {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.action-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #374151;
}

.action-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  background: #6366f1;
  color: white;
  border-radius: 50%;
  font-size: 0.65rem;
  font-weight: 600;
  flex-shrink: 0;
}

.action-text {
  line-height: 1.4;
}

/* Expirations Section */
.expirations-list {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.expiration-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  border-radius: 4px;
  background: #f8fafc;
  border-left: 3px solid #e2e8f0;
}

.expiration-item.urgency-critical {
  border-left-color: #dc2626;
  background: #fef2f2;
}

.expiration-item.urgency-high {
  border-left-color: #f97316;
  background: #fff7ed;
}

.expiration-item.urgency-medium {
  border-left-color: #f59e0b;
  background: #fffbeb;
}

.expiration-date {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #334155;
  white-space: nowrap;
}

.expiration-date i {
  color: #64748b;
  font-size: 0.7rem;
}

.expiration-info {
  flex: 1;
  min-width: 0;
}

.expiration-name {
  font-size: 0.75rem;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.expiration-days {
  font-size: 0.65rem;
  color: #64748b;
}

/* Trend Section */
.trend-text {
  font-size: 0.8rem;
  color: #475569;
  margin: 0 0 0.5rem 0;
  line-height: 1.5;
}

.trend-projections {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.projection-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
}

.projection-label {
  color: #64748b;
}

.projection-value {
  font-weight: 600;
}

.projection-value.excellent { color: #059669; }
.projection-value.good { color: #2563eb; }
.projection-value.warning { color: #d97706; }
.projection-value.at-risk { color: #ea580c; }
.projection-value.critical { color: #dc2626; }

/* Metadata Section */
.metadata-section {
  background: transparent;
  border: none;
  padding: 0;
}

.metadata-items {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.metadata-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.7rem;
  color: #94a3b8;
}

.metadata-item i {
  font-size: 0.65rem;
}

/* Empty State */
.detail-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem;
  color: #94a3b8;
  font-size: 0.875rem;
}

/* ================================================
   Compliance Configuration Section
   ================================================ */
.config-section {
  background: #f8fafc;
}

.config-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.375rem 0;
  border-bottom: 1px solid #f1f5f9;
}

.config-row:last-child { border-bottom: none; }

.config-row-top { align-items: flex-start; }

.config-label {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #475569;
  min-width: 140px;
}

.config-value {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  flex-wrap: wrap;
}

.config-empty { font-size: 0.75rem; color: #94a3b8; font-style: italic; }

.profile-badge {
  background: #ede9fe;
  color: #5b21b6;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
}

.waiver-count-badge {
  background: #f1f5f9;
  color: #64748b;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.7rem;
}
.waiver-count-badge.has-waivers {
  background: #dcfce7;
  color: #166534;
}

.override-count-badge {
  background: #fef3c7;
  color: #92400e;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
}

.btn-config-edit {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.125rem 0.25rem;
  font-size: 0.7rem;
  transition: color 0.15s;
}
.btn-config-edit:hover { color: #3b82f6; }

/* Waiver compact list */
.agent-policy-notes {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.agent-policy-hint {
  margin: 0;
  font-size: 0.8rem;
  color: #475569;
  line-height: 1.4;
}

.agent-policy-note {
  font-size: 0.8rem;
  color: #334155;
  line-height: 1.4;
}

.waiver-list-compact {
  padding: 0.375rem 0 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.waiver-chip {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: #dcfce7;
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  padding: 0.125rem 0.375rem 0.125rem 0.5rem;
  font-size: 0.7rem;
}

.waiver-code { color: #166534; font-weight: 600; }
.waiver-expiry { color: #4ade80; font-size: 0.65rem; }

.btn-revoke-inline {
  background: none;
  border: none;
  color: #16a34a;
  cursor: pointer;
  padding: 0;
  font-size: 0.65rem;
  opacity: 0.6;
  transition: opacity 0.15s;
}
.btn-revoke-inline:hover { opacity: 1; }

.btn-see-all {
  background: none;
  border: none;
  color: #3b82f6;
  font-size: 0.7rem;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
}

/* Limits compact display */
.limits-compact {
  padding: 0.375rem 0 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.limit-chip {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  background: #fef9c3;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 0.15rem 0.5rem;
  font-size: 0.7rem;
}

.limit-coverage { font-weight: 700; color: #78350f; }
.limit-value { color: #92400e; }

/* ================================================
   Modals
   ================================================ */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.config-modal {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 560px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
}

.config-modal-sm { max-width: 420px; }

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 1rem;
  color: #1e293b;
}

.btn-modal-close {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.25rem;
}
.btn-modal-close:hover { color: #475569; }

.modal-body { padding: 1.25rem; }

.modal-hint {
  font-size: 0.8rem;
  color: #64748b;
  margin: 0 0 1rem 0;
  line-height: 1.5;
}

/* Waiver table */
.waiver-table { margin-bottom: 1.5rem; }
.waiver-table h4 { font-size: 0.85rem; color: #374151; margin: 0 0 0.75rem 0; }

.waiver-row {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
}

.waiver-row-main {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
  font-size: 0.85rem;
  color: #1e293b;
}

.waiver-scope-badge {
  background: #ede9fe;
  color: #5b21b6;
  padding: 0.1rem 0.4rem;
  border-radius: 8px;
  font-size: 0.65rem;
  font-weight: 600;
}

.waiver-row-meta {
  font-size: 0.75rem;
  color: #64748b;
  margin-bottom: 0.5rem;
}

.btn-revoke {
  background: #fee2e2;
  color: #dc2626;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}
.btn-revoke:hover:not(:disabled) { background: #fecaca; }
.btn-revoke:disabled { opacity: 0.5; cursor: not-allowed; }

.empty-waivers {
  text-align: center;
  color: #94a3b8;
  font-size: 0.85rem;
  padding: 1rem 0;
  margin-bottom: 1rem;
}

/* Add waiver form */
.add-waiver-form { border-top: 1px solid #e2e8f0; padding-top: 1rem; }
.add-waiver-form h4 { font-size: 0.85rem; color: #374151; margin: 0 0 0.75rem 0; }

.form-group { margin-bottom: 0.75rem; }
.form-group label { display: block; font-size: 0.75rem; font-weight: 600; color: #475569; margin-bottom: 0.25rem; }

.form-input, .form-textarea, .form-select {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.8rem;
  color: #1e293b;
  background: white;
  box-sizing: border-box;
}
.form-input:focus, .form-textarea:focus, .form-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59,130,246,0.15);
}
.form-input-sm {
  padding: 0.3rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.75rem;
  width: 120px;
}

.btn-add-waiver, .btn-save-profile, .btn-save-limits {
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1.25rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.5rem;
}
.btn-add-waiver:hover:not(:disabled), .btn-save-profile:hover:not(:disabled), .btn-save-limits:hover:not(:disabled) {
  background: #2563eb;
}
.btn-add-waiver:disabled, .btn-save-profile:disabled, .btn-save-limits:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Profile options */
.profile-options { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 1rem; }

.profile-option {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  padding: 0.625rem 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s;
}
.profile-option.selected { border-color: #3b82f6; background: #eff6ff; }
.profile-option input[type="radio"] { display: none; }
.profile-option-name { font-size: 0.85rem; font-weight: 600; color: #1e293b; }
.profile-option-desc { font-size: 0.75rem; color: #64748b; }
.profile-option-coverages { font-size: 0.7rem; color: #3b82f6; }

.empty-profiles { font-size: 0.8rem; color: #94a3b8; text-align: center; padding: 1rem 0; }
.empty-profiles a { color: #3b82f6; }

/* Limits editor */
.limits-editor { margin-bottom: 1rem; }

.limit-edit-row {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
}

.limit-edit-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.limit-coverage-label { font-size: 0.85rem; font-weight: 700; color: #1e293b; }

.limit-edit-field {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.375rem;
}
.limit-edit-field label { font-size: 0.75rem; color: #475569; min-width: 130px; }

.btn-remove-coverage, .btn-remove-limit {
  background: none;
  border: none;
  color: #dc2626;
  cursor: pointer;
  font-size: 0.7rem;
  opacity: 0.6;
}
.btn-remove-coverage:hover, .btn-remove-limit:hover { opacity: 1; }

.btn-add-limit-field {
  background: none;
  border: 1px dashed #d1d5db;
  color: #64748b;
  border-radius: 4px;
  padding: 0.2rem 0.5rem;
  font-size: 0.7rem;
  cursor: pointer;
  margin-top: 0.25rem;
}
.btn-add-limit-field:hover { border-color: #3b82f6; color: #3b82f6; }

.add-coverage-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.btn-add-coverage {
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
  font-size: 0.75rem;
  cursor: pointer;
}

.modal-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f1f5f9;
}

.btn-clear-overrides {
  background: none;
  border: 1px solid #dc2626;
  color: #dc2626;
  border-radius: 6px;
  padding: 0.4rem 0.875rem;
  font-size: 0.75rem;
  cursor: pointer;
}
.btn-clear-overrides:hover:not(:disabled) { background: #fee2e2; }
.btn-clear-overrides:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
